import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, AlertTriangle, ShieldCheck, FileWarning, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

/**
 * Phase 2 — telecom_v2 PM rollups panel.
 *
 * Lazy-loads pm_get_assessment_rollups(assessment_id) and renders:
 *   • CSV Cybersecurity Domains (8)
 *   • NIST Functional Ratings (6)
 *   • Findings summary
 *   • CAP summary
 *
 * Self-contained, additive — does not modify the parent dashboard layout.
 */

type CsvDomain = {
  domain_name: string;
  average_score: number | null;
  maturity_level: string | null;
  controls_evaluated: number | null;
};

type NistFn = {
  function_name: string;
  average_score: number | null;
  maturity_level: string | null;
  controls_evaluated: number | null;
};

export type PmAssessmentRollups = {
  assessment_id: string;
  questionnaire_id: string | null;
  assessment_status: string | null;
  assessment_phase: string | null;
  csv_domains: CsvDomain[];
  nist_functions: NistFn[];
  findings_summary: {
    total: number;
    by_risk_level: Record<string, number>;
    by_csv_domain: Record<string, number>;
    by_nist_function: Record<string, number>;
    overdue_count: number;
    awaiting_validation_count: number;
  };
  cap_summary: {
    published: boolean;
    published_at: string | null;
    version: number | null;
    items_total: number;
    items_open: number;
    items_in_progress: number;
    items_submitted_for_review: number;
    items_validated: number;
    items_closed: number;
    items_overdue: number;
  };
};

const CSV_ORDER = [
  "Risk Management",
  "Governance",
  "Supply Chain Management",
  "Asset Management",
  "Identity-Centric Security",
  "Security Operations",
  "Data Protection",
  "Infrastructure Security",
];

const NIST_ORDER = ["Govern", "Identify", "Protect", "Detect", "Respond", "Recover"];

// Maturity → semantic color (per spec: Minimal=red, Adequate=orange, Strong=green, Extensive=blue)
function maturityClasses(level: string | null | undefined): string {
  switch (level) {
    case "Extensive":
      return "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800";
    case "Strong":
      return "bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800";
    case "Adequate":
      return "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800";
    case "Minimal":
      return "bg-red-100 text-red-700 border-red-300 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
}

export default function PmAssessmentRollupsPanel({
  assessmentId,
}: {
  assessmentId: string;
}) {
  const [data, setData] = useState<PmAssessmentRollups | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data: res, error: err } = await (supabase.rpc as any)(
        "pm_get_assessment_rollups",
        { p_assessment_id: assessmentId }
      );
      if (cancelled) return;
      if (err) {
        console.error("[PM] pm_get_assessment_rollups error", err);
        setError(err.message || "Failed to load rollups");
      } else {
        setData(res as PmAssessmentRollups);
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading assessment rollups…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="py-4 text-sm text-destructive">
        {error ?? "No rollup data available."}
      </div>
    );
  }

  // Index domain/fn rows so we can render in canonical order, even if some are missing
  const csvByName = new Map(data.csv_domains.map((d) => [d.domain_name, d]));
  const nistByName = new Map(data.nist_functions.map((n) => [n.function_name, n]));

  const fs = data.findings_summary;
  const high = (fs.by_risk_level?.["High"] ?? 0) + (fs.by_risk_level?.["Critical"] ?? 0);
  const moderate = fs.by_risk_level?.["Moderate"] ?? fs.by_risk_level?.["Medium"] ?? 0;
  const low = fs.by_risk_level?.["Low"] ?? 0;

  const cap = data.cap_summary;

  // Top 3 CSV domains by finding count
  const topCsvFindings = Object.entries(fs.by_csv_domain ?? {})
    .sort((a, b) => (b[1] as number) - (a[1] as number))
    .slice(0, 3);

  return (
    <div className="space-y-5 py-4">
      {/* Phase chip */}
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          Assessment Phase
        </span>
        <AssessmentPhaseChip phase={data.assessment_phase} />
      </div>

      {/* CSV Cybersecurity Domains */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2 flex items-center gap-2">
          <ShieldCheck className="h-3.5 w-3.5" />
          CSV Cybersecurity Domains
        </h4>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {CSV_ORDER.map((name) => {
            const row = csvByName.get(name);
            const cls = maturityClasses(row?.maturity_level);
            return (
              <div
                key={name}
                className={`rounded-md border px-3 py-2 ${cls}`}
                title={
                  name === "Infrastructure Security"
                    ? "Scored directly from the Infrastructure tab (telecom_v2)."
                    : undefined
                }
              >
                <div className="text-xs font-medium leading-tight">{name}</div>
                <div className="mt-1">
                  <span className="text-xs font-semibold">
                    {row?.maturity_level ?? "—"}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] opacity-70">
                  {row?.controls_evaluated ?? 0} control
                  {(row?.controls_evaluated ?? 0) === 1 ? "" : "s"} evaluated
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* NIST Functional Ratings */}
      <section>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
          NIST Functional Ratings
        </h4>
        <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {NIST_ORDER.map((name) => {
            const row = nistByName.get(name);
            const cls = maturityClasses(row?.maturity_level);
            return (
              <div key={name} className={`rounded-md border px-2.5 py-2 ${cls}`}>
                <div className="text-xs font-medium">{name}</div>
                <div className="mt-1">
                  <span className="text-[11px] font-semibold">
                    {row?.maturity_level ?? "—"}
                  </span>
                </div>
                <div className="mt-0.5 text-[10px] opacity-70">
                  {row?.controls_evaluated ?? 0} control
                  {(row?.controls_evaluated ?? 0) === 1 ? "" : "s"} evaluated
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Findings + CAP summary */}
      <div className="grid gap-3 md:grid-cols-2">
        {/* Findings */}
        <div className="rounded-md border bg-card p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
              <FileWarning className="h-3.5 w-3.5" />
              Findings
            </h4>
            <Badge variant="outline" className="text-xs">
              {fs.total} total
            </Badge>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 py-1.5">
              <div className="text-base font-semibold text-red-700 dark:text-red-300">
                {high}
              </div>
              <div className="text-[10px] uppercase text-red-700/70 dark:text-red-300/70">
                High / Critical
              </div>
            </div>
            <div className="rounded bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 py-1.5">
              <div className="text-base font-semibold text-orange-700 dark:text-orange-300">
                {moderate}
              </div>
              <div className="text-[10px] uppercase text-orange-700/70 dark:text-orange-300/70">
                Moderate
              </div>
            </div>
            <div className="rounded bg-muted py-1.5 border border-border">
              <div className="text-base font-semibold">{low}</div>
              <div className="text-[10px] uppercase text-muted-foreground">Low</div>
            </div>
          </div>
          {topCsvFindings.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase font-semibold text-muted-foreground mb-1">
                Top CSV domains
              </div>
              <div className="flex flex-wrap gap-1.5">
                {topCsvFindings.map(([dom, count]) => (
                  <Badge key={dom} variant="secondary" className="text-[10px]">
                    {dom}: {count as number}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* CAP */}
        <div className="rounded-md border bg-card p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Corrective Action Plan
            </h4>
            <Badge
              className={
                cap.published
                  ? "bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800 text-xs"
                  : "bg-muted text-muted-foreground border-border text-xs"
              }
            >
              {cap.published ? "Published" : "Not Published"}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total items</span>
              <span className="font-semibold">{cap.items_total}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Open</span>
              <span className="font-semibold">{cap.items_open}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">In progress</span>
              <span className="font-semibold">{cap.items_in_progress}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Awaiting review</span>
              <span
                className={
                  cap.items_submitted_for_review > 0
                    ? "font-semibold text-orange-600 dark:text-orange-400"
                    : "font-semibold"
                }
              >
                {cap.items_submitted_for_review}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Validated</span>
              <span className="font-semibold text-green-600 dark:text-green-400">
                {cap.items_validated}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Closed</span>
              <span className="font-semibold">{cap.items_closed}</span>
            </div>
            <div className="flex justify-between col-span-2">
              <span className="text-muted-foreground flex items-center gap-1">
                {cap.items_overdue > 0 && (
                  <AlertTriangle className="h-3 w-3 text-red-600 dark:text-red-400" />
                )}
                Overdue
              </span>
              <span
                className={
                  cap.items_overdue > 0
                    ? "font-semibold text-red-600 dark:text-red-400"
                    : "font-semibold"
                }
              >
                {cap.items_overdue}
              </span>
            </div>
          </div>
          {cap.published && (
            <div className="mt-3 pt-3 border-t border-border">
              <Button asChild size="sm" variant="outline" className="w-full">
                <Link to={`/action-plan/${data.assessment_id}?mode=pm`}>
                  <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                  View Corrective Action Plan
                </Link>
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Standalone helper: phase chip.
 * Reused by the dashboard table to show the 7-state phase derived from the rollup RPC.
 */
export function AssessmentPhaseChip({ phase }: { phase: string | null | undefined }) {
  const styles: Record<string, string> = {
    "Not Started": "bg-muted text-muted-foreground border-border",
    "In Progress": "bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800",
    Submitted: "bg-indigo-100 text-indigo-700 border-indigo-300 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800",
    "In Review": "bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800",
    "CAP Published": "bg-orange-100 text-orange-700 border-orange-300 dark:bg-orange-950/40 dark:text-orange-300 dark:border-orange-800",
    Remediation: "bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800",
    Closed: "bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800",
  };
  const label = phase || "Not Started";
  const cls = styles[label] || styles["Not Started"];
  return <Badge className={`border text-xs ${cls}`}>{label}</Badge>;
}
